import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../style/OtpForm.css';
import { sendOtp, verifyOtp, userSave } from '../types/api';
import { useUser } from '../context/UserContext';
import { useUserLogin } from '../context/AuthContext';
import axios from 'axios';
import '../style/question.css';
import { useLanguage } from "../context/LanguageContext";
import { languageOptionsMap } from "../i18n/languageOptions";
import { languageMap } from "../i18n/languageMap";
import { countryCodeMap } from "../types/countryCode";

const OTPForm: React.FC = () => {
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(30);
  const [resendAvailable, setResendAvailable] = useState(false);
  const inputRefs = useRef<HTMLInputElement[]>([]);
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');

  const { updateUser } = useUser();
  const { setUserLogin } = useUserLogin();
  const { country, setCountry, language, setLanguage } = useLanguage();

  const [state, setState] = useState<string>('');
  const [district, setDistrict] = useState<string>('');
  const [city, setCity] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [isEditable, setIsEditable] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [labels, setLabels] = useState<Record<string, string>>({});

  const isWaitOtp = process.env.REACT_APP_SHOW_OTP === 'true'; // or false based on logic

  // Load language labels
  useEffect(() => {
    setUserLogin(null);
    if (country && language) {
      setLabels(languageMap[country]?.[language] || {});
    }
  }, [country, language, setUserLogin]);

  // Timer for OTP resend
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (otpSent && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    } else if (timer === 0) {
      setResendAvailable(true);
      if (interval) clearInterval(interval);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [otpSent, timer]);

  // Handle pincode lookup
  const handlePincodeChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const pin = e.target.value;
    setPinCode(pin);

    if (pin.length === 6) {
      try {
        if (country === 'IN') {
          const response = await axios.get(`https://api.postalpincode.in/pincode/${pin}`);
          const data = response.data[0];
          if (data.Status === 'Success') {
            const postOffice = data.PostOffice[0];
            setState(postOffice.State);
            setDistrict(postOffice.District);
          } else throw new Error('Invalid pinCode for India');
        } else {
          const response = await axios.get(`https://api.zippopotam.us/${country}/${pin}`);
          const { places } = response.data;
          if (places?.length > 0) {
            setState(places[0].state);
            setCity(places[0]['place name']);
            setDistrict('');
          } else throw new Error('Invalid pinCode for selected country');
        }
        setErrorMessage('');
      } catch (error) {
        console.error('Error fetching location:', error);
        setState('');
        setDistrict('');
        setCity('');
        setErrorMessage('Failed to fetch location details. Please check pinCode and country.');
      }
    }
  };

  // Country & language
  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCountry = e.target.value;
    setCountry(selectedCountry);

    setState('');
    setDistrict('');
    setCity('');
    setPinCode('');
    setMobile('');
    setWhatsapp('');
    setLanguage("");

    if (selectedCountry) {
      setIsEditable(true);
      const code = countryCodeMap[selectedCountry] || '';
      setMobile(code);
      setWhatsapp(code);
    } else {
      setIsEditable(false);
    }
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLanguage(e.target.value);
  };

  // Input handlers
  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    if (!input.startsWith(countryCodeMap[country])) {
      setMobile(countryCodeMap[country] + input.replace(/^\+?\d*/, ''));
    } else {
      setMobile(input);
    }
  };

  const handlewhatsAppChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(' WhatsApp Number', '');
    if (!input.startsWith(countryCodeMap[country])) {
      setWhatsapp(countryCodeMap[country] + input.replace(/^\+?\d*/, ''));
    } else {
      setWhatsapp(input);
    }
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const { value } = e.target;
    if (/^[0-9]{1}$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (index < inputRefs.current.length - 1) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && otp[index] === '' && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  // API handlers
  const handleSendOtp = async (e: React.MouseEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!name || !mobile || !country || !pinCode || !city) {
      setErrorMessage(labels.errorMessage || 'Name, Country, Mobile Number, Pincode and City cannot be empty.');
      return;
    }

    try {
      setLoading(true);
      const response = await sendOtp(name, mobile, whatsapp, email, country);
      if (response?.status === 200) {
        setOtpSent(true);
        setResendAvailable(false);
        setTimer(30);
        await userSave(name, mobile, whatsapp, email, country, state, district, city, pinCode, 'SENT_OTP');
      } else {
        setErrorMessage(labels.errorFailedOTP || 'Failed to send OTP.');
        await userSave(name, mobile, whatsapp, email, country, state, district, city, pinCode, 'OTP_SEND_FAILED');
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      setErrorMessage(labels.errorFailedOTP || 'Failed to send OTP.');
      await userSave(name, mobile, whatsapp, email, country, state, district, city, pinCode, 'OTP_SEND_FAILED');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpCode = otp.join('');
    try {
      setLoading(true);
      await verifyOtp(name, mobile, whatsapp, email, otpCode);

      updateUser({ name, mobile, whatsapp, email, state, district, city, pinCode });

      navigate('/video', { replace: true });
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setErrorMessage(labels.errorMessage || 'Invalid OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleDirectSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setOtpSent(true);
      setLoading(true);

      updateUser({ name, mobile, whatsapp, language, email, country, state, district, city, pinCode });

      await userSave(name, mobile, whatsapp, email, country, state, district, city, pinCode, 'OTP_FORM_COMPLETED');

      navigate('/video', { replace: true });
    } catch (error) {
      console.error('Error submitting without OTP:', error);
      setErrorMessage('Submission failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = (e: React.MouseEvent) => {
    e.preventDefault();
    setResendAvailable(false);
    setTimer(30);
    handleSendOtp(e);
  };

  return (
    <div className="div-container">
      <form onSubmit={isWaitOtp ? handleSubmit : handleDirectSubmit} className="otp-form">
        {errorMessage && <p style={{ color: 'red', top: '30px', position: 'absolute' }}>{errorMessage}</p>}

        {/* Country Selector */}
        <select className="otp-input" value={country} onChange={handleCountryChange} required>
          <option value="">Select the Country</option>
          <option value="IN">India</option>
          <option value="AE">United Arab Emirates (UAE)</option>
          <option value="SA">Kingdom of Saudi Arabia</option>
          <option value="BD">Bangladesh</option>
          <option value="TH">Thailand</option>
          <option value="GH">Ghana</option>
          <option value="PH">Philippines</option>
          <option value="CI">Ivory Coast (Côte d'Ivoire)</option>
          <option value="NP">Nepal</option>
        </select>

        {/* Language Selector */}
        <select value={language} onChange={handleLanguageChange} required>
          <option value="">Select Language</option>
          <option value="EN">English</option>
          {Object.entries(languageOptionsMap[country] || {}).map(([lang, nativeLang]) => (
            <option key={lang} value={lang}>
              {`${lang} (${nativeLang})`}
            </option>
          ))}
        </select>

        {/* Inputs */}
        <input className="otp-input" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={labels.name || 'Name'} required />
        <input className="otp-input" type="text" value={mobile} onChange={handleMobileChange} placeholder={labels.mobileNumber || 'Mobile Number'} readOnly={!isEditable} required maxLength={14} />
        <input className="otp-input" type="text" value={whatsapp} onChange={handlewhatsAppChange} placeholder={labels.whatsappNumber || 'WhatsApp Number'} readOnly={!isEditable} required maxLength={14} />
        <input className="otp-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={labels.email || 'Email'} />
        <input className="otp-input" type="text" value={pinCode} onChange={handlePincodeChange} placeholder={labels.pinCode || 'Pincode'} readOnly={!isEditable} required />
        <input className="otp-input" type="text" value={state} readOnly placeholder={labels.state || 'State'} required />
        <input className="otp-input" type="text" value={district} readOnly placeholder={labels.district || 'District'} required />
        <input className="otp-input" type="text" value={city} readOnly={!isEditable} onChange={(e) => setCity(e.target.value)} placeholder={labels.city || 'City'} required />

        {/* OTP Logic */}
        {isWaitOtp && !otpSent && (
          <a onClick={handleSendOtp} style={{ backgroundColor: '#e83026', color: 'white', padding: '10px 32px', borderRadius: '8px', cursor: 'pointer' }}>
            {loading ? 'Sending...' : labels.sendOtp || 'Send OTP'}
          </a>
        )}

        {!isWaitOtp && (
          <button type="submit" className="otp-button" disabled={loading}>
            {loading ? 'Loading...' : labels.continue || 'Submit'}
          </button>
        )}
      
        {isWaitOtp && otpSent && (
          <>
            <label htmlFor="otp">{labels.enterOtp || 'Please enter the 6 digit code'}</label>
            <div className="otp-box-container">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  id="otp"
                  maxLength={1}
                  className="otp-box"
                  value={digit}
                  onChange={(e) => handleOtpChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  ref={(el) => (inputRefs.current[index] = el!)}
                  required
                />
              ))}
            </div>

            <button type="submit" className="otp-button" disabled={loading}>
              {loading ? 'Loading...' : labels.continue || 'Continue'}
            </button>

            {!resendAvailable ? (
              <p>{labels.resendOtp ? labels.resendOtp.replace('{timer}', `${timer}`) : `Resend OTP in ${timer} seconds`}</p>
            ) : (
              <div>
                <p>{labels.resendOtp || "Didn't receive the code?"}</p>
                <div style={{ cursor: 'pointer', textAlign: 'center' }}>
                  <a onClick={handleResendOtp} style={{ color: '#e83026', cursor: 'pointer' }}>
                    {labels.resendOtp || 'Resend OTP'}
                  </a>
                </div>
              </div>
            )}
          </>
        )}
      </form>
    </div>
  );
};

export default OTPForm;
